import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './salesChannelReconciliationReport.module.scss';
import i18n from 'i18n';
import moment from 'moment';
import { DateRangePickerField } from 'components/common/form/field/DateRangePickerField';
import { CustomField } from 'components/common/form/field/CustomField';
import { Button } from 'react-bootstrap';
import { DefaultReportManager } from 'core/report/ReportManager';
import { useCallAPI } from 'hooks/useCallAPI';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import { Trans } from 'react-i18next';
import { MonthPicker } from 'components/common/MonthPicker/MonthPicker';
import { ReportGran } from 'core/report/ReportData';
import ToggleButtons from 'components/common/ToggleButtons/ToggleButtons';
import { Page } from 'components/Page/Page';
import { SelectField } from 'components/common/form/field/SelectField';
import { DefaultChannelGroupManager } from 'core/channelGroup/ChannelGroupManager';

const defaultReportManager = new DefaultReportManager();
const defaultChannelGroupManager = new DefaultChannelGroupManager();

export const SalesChannelReconciliationReport: React.FC = () => {
  const [allowedSalesChannels, setAllowedSalesChannels] = useState<
    SelectOptions[]
  >([]);
  const [salesChannel, setSalesChannel] = useState<number>();
  const [gran, setGran] = useState<string | number>(ReportGran.MONTH);
  const [from, setFrom] = useState<string>(
    moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD')
  );
  const [to, setTo] = useState<string>(
    moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
  );
  const { loading, callAPIs } = useCallAPI();

  const maxDate = moment().endOf('day').subtract(1, 'day').format('YYYY-MM-DD');

  useEffect(() => {
    callAPIs(
      // ECOPD-5566：對帳報表要查得到停用渠道的歷史資料，
      // 所以走含停用渠道的 report-channels，而不是排除停用渠道的 available-channels。
      // label 的停用後綴由 `getReportChannels` 接上（前端），這裡逐字渲染。
      [() => defaultChannelGroupManager.getReportChannels()],
      (reportChannels: SelectOptions[]) => {
        setAllowedSalesChannels(reportChannels);
        reportChannels[0] && setSalesChannel(+reportChannels[0].value);
      }
    );
  }, [callAPIs]);

  const onSalesChannelChange = useCallback((value: number) => {
    setSalesChannel(value);
  }, []);

  const reportGranOptions = useMemo(() => {
    return Object.values(ReportGran)
      .filter((reportGran) => reportGran !== ReportGran.HOUR)
      .map((reportGran) => {
        return {
          label: i18n.t(`report.labels.${reportGran}`),
          value: reportGran
        };
      });
  }, []);

  const renderMonthPicker = useCallback(() => {
    const handleMonthChange = (date: string) => {
      const from = moment(date).startOf('month').format('YYYY-MM-DD');
      const to = moment(date).endOf('month').format('YYYY-MM-DD');
      setFrom(from);
      setTo(to);
    };
    return (
      <MonthPicker date={from} maxDate={maxDate} onChange={handleMonthChange} />
    );
  }, [from, maxDate]);

  const renderDateRangePicker = useCallback(() => {
    const renderScheduling = () => {
      const endDateMoment = moment(to);
      const startDateMoment = moment(from);
      const days = endDateMoment.diff(startDateMoment, 'days');
      if (days > 0) {
        return (
          <Trans
            i18nKey='campaign.descriptions.campaignSchedulingDay'
            values={{ days: days + 1 }}
            components={{
              1: <span className='text-dark' />
            }}
          />
        );
      }
    };

    const handleDateChange = (
      from: string | undefined,
      to: string | undefined
    ) => {
      from && setFrom(moment(from).startOf('day').format('YYYY-MM-DD'));
      to && setTo(moment(to).endOf('day').format('YYYY-MM-DD'));
    };

    return (
      <DateRangePickerField
        startDate={from}
        endDate={to}
        maxDate={moment().endOf('day').subtract(1, 'day').format('YYYY-MM-DD')}
        format={'YYYY-MM-DD'}
        onChange={handleDateChange}
        showTimePicker={false}
        hint={renderScheduling()}
        fieldContentWidth={184}
      />
    );
  }, [from, to]);

  const reportGranDatePickerElementMap: {
    [key: string | number]: JSX.Element;
  } = useMemo(() => {
    return {
      [ReportGran.MONTH]: renderMonthPicker(),
      [ReportGran.DAY]: renderDateRangePicker()
    };
  }, [renderMonthPicker, renderDateRangePicker]);

  const downloadReport = useCallback(() => {
    if (!salesChannel) {
      return;
    }
    callAPIs([
      () =>
        defaultReportManager.downloadSalesChannelReconciliationReport(
          gran as ReportGran,
          salesChannel,
          from,
          to
        )
    ]);
  }, [gran, salesChannel, from, to, callAPIs]);

  return (
    <Page title={i18n.t('report.titles.salesChannelReconciliation')}>
      {loading && <LoadingIndicator />}
      <Page.Section>
        <CustomField
          label={i18n.t('report.labels.reportGran')}
          name='reportGran'
        >
          <div className={styles.reportGran}>
            <ToggleButtons
              name='reportGran'
              value={gran}
              options={reportGranOptions}
              onChange={setGran}
            />
            {reportGranDatePickerElementMap[gran]}
          </div>
        </CustomField>
        <SelectField
          label={i18n.t('report.labels.salesChannel')}
          name='salesChannel'
          options={allowedSalesChannels}
          value={salesChannel}
          simpleValue
          onChange={onSalesChannelChange}
          hint={i18n.t('report.hints.salesChannelReconciliation')}
        />
        <CustomField
          formGroupClassName={styles.reportSearchBtnContainer}
          label=''
          name='reportDownloadButton'
        >
          <Button variant='primary' onClick={downloadReport} size='sm'>
            {i18n.t('report.labels.download')}
          </Button>
        </CustomField>
      </Page.Section>
    </Page>
  );
};
